<template>
  <page-header-wrapper>
    <a-card :bordered="false">
      <!-- 条件搜索 -->
      <div class="table-page-search-wrapper">
        <a-form layout="inline">
          <a-row :gutter="48">
            <a-col :md="6" :sm="24">
              <a-form-item label="退货单号">
                <a-input v-model="queryParam.returnNo" placeholder="请输入退货单号" allow-clear />
              </a-form-item>
            </a-col>
            <a-col :md="6" :sm="24">
              <a-form-item label="供应商">
                <a-input v-model="queryParam.supplierName" placeholder="请输入供应商名称" allow-clear />
              </a-form-item>
            </a-col>
            <a-col :md="6" :sm="24">
              <a-form-item label="状态">
                <a-select v-model="queryParam.returnStatus" placeholder="请选择状态" allow-clear style="width: 100%">
                  <a-select-option :value="0">待审核</a-select-option>
                  <a-select-option :value="1">审核通过</a-select-option>
                  <a-select-option :value="2">审核不通过</a-select-option>
                  <a-select-option :value="3">已取消</a-select-option>
                  <a-select-option :value="4">已退货入库</a-select-option>
                </a-select>
              </a-form-item>
            </a-col>
            <a-col :md="6" :sm="24">
              <span class="table-page-search-submitButtons">
                <a-button type="primary" @click="handleQuery">
                  <a-icon type="search" />查询
                </a-button>
                <a-button style="margin-left: 8px" @click="resetQuery">
                  <a-icon type="redo" />重置
                </a-button>
              </span>
            </a-col>
          </a-row>
        </a-form>
      </div>

      <!-- 操作 -->
      <div class="table-operations">
        <a-button type="primary" @click="handleAdd" v-hasPermi="['retail:inventory-return:add']">
          <a-icon type="plus" />采购退货
        </a-button>
      </div>

      <!-- 采购退货表单 -->
      <return-form ref="returnForm" @ok="getList" />

      <!-- 记录列表 -->
      <a-table
        :loading="loading"
        :size="tableSize"
        rowKey="id"
        :columns="columns"
        :data-source="list"
        :pagination="pagination"
        :bordered="tableBordered"
        @change="handleTableChange"
      >
        <span slot="returnStatus" slot-scope="text">
          <a-tag :color="getStatusColor(text)">{{ getStatusText(text) }}</a-tag>
        </span>
        <span slot="returnReason" slot-scope="text">
          {{ getReasonText(text) }}
        </span>
        <span slot="totalAmount" slot-scope="text">
          ¥{{ text ? parseFloat(text).toFixed(2) : '0.00' }}
        </span>
        <span slot="applyTime" slot-scope="text">
          {{ parseTime(text) }}
        </span>
        <span slot="operation" slot-scope="text, record">
          <a @click="handleDetail(record)" style="margin-right: 8px">
            <a-icon type="eye" />查看详情
          </a>
          <a v-if="record.returnStatus === 0" @click="handleAudit(record)" style="margin-right: 8px" v-hasPermi="['retail:inventory-return:audit']">
            <a-icon type="check" />审核通过
          </a>
          <a v-if="record.returnStatus === 0" @click="handleReject(record)" v-hasPermi="['retail:inventory-return:audit']">
            <a-icon type="close" />审核拒绝
          </a>
        </span>
      </a-table>
    </a-card>

    <!-- 明细弹窗 -->
    <a-modal
      :title="`退货明细 - ${currentRecord?.returnNo || ''}`"
      :visible="detailVisible"
      :footer="null"
      @cancel="detailVisible = false"
      width="1000px"
      :centered="true"
    >
      <a-descriptions bordered :column="2" style="margin-bottom: 16px">
        <a-descriptions-item label="退货单号">{{ currentRecord?.returnNo }}</a-descriptions-item>
        <a-descriptions-item label="供应商">{{ currentRecord?.supplierName || '-' }}</a-descriptions-item>
        <a-descriptions-item label="状态">
          <a-tag :color="getStatusColor(currentRecord?.returnStatus)">{{ getStatusText(currentRecord?.returnStatus) }}</a-tag>
        </a-descriptions-item>
        <a-descriptions-item label="申请人">{{ currentRecord?.applicantName || '-' }}</a-descriptions-item>
        <a-descriptions-item label="退货原因">{{ getReasonText(currentRecord?.returnReason) }}</a-descriptions-item>
        <a-descriptions-item label="退货原因描述">{{ currentRecord?.returnReasonDesc || '-' }}</a-descriptions-item>
        <a-descriptions-item label="退货数量">{{ currentRecord?.totalQuantity }}</a-descriptions-item>
        <a-descriptions-item label="退货金额">{{ currentRecord?.totalAmount ? '¥' + parseFloat(currentRecord.totalAmount).toFixed(2) : '¥0.00' }}</a-descriptions-item>
        <a-descriptions-item label="备注" :span="2">{{ currentRecord?.remark || '-' }}</a-descriptions-item>
        <a-descriptions-item label="审核人">{{ currentRecord?.approverName || '-' }}</a-descriptions-item>
        <a-descriptions-item label="审核备注">{{ currentRecord?.approveRemark || '-' }}</a-descriptions-item>
        <a-descriptions-item label="申请时间">{{ parseTime(currentRecord?.applyTime) }}</a-descriptions-item>
        <a-descriptions-item label="审核时间">{{ parseTime(currentRecord?.approveTime) }}</a-descriptions-item>
      </a-descriptions>

      <a-table
        :loading="detailLoading"
        rowKey="id"
        :columns="detailColumns"
        :data-source="detailList"
        :pagination="false"
        size="small"
      >
        <span slot="goodsName" slot-scope="text, record">
          {{ text }} <a-tag v-if="record.skuName" color="orange">{{ record.skuName }}</a-tag>
        </span>
        <span slot="returnCount" slot-scope="text">
          <a-tag color="blue">{{ text }}</a-tag>
        </span>
        <span slot="unitPrice" slot-scope="text">
          ¥{{ text ? parseFloat(text).toFixed(2) : '0.00' }}
        </span>
        <span slot="amount" slot-scope="text">
          ¥{{ text ? parseFloat(text).toFixed(2) : '0.00' }}
        </span>
      </a-table>
    </a-modal>
  </page-header-wrapper>
</template>

<script>
import { getReturnOrderList, getReturnItemList, auditReturnOrder, rejectReturnOrder } from '@/api/retail/inventory'
import ReturnForm from './modules/ReturnForm'
import { tableMixin } from '@/store/table-mixin'
import moment from 'moment'

export default {
  name: 'InventoryReturn',
  components: {
    ReturnForm
  },
  mixins: [tableMixin],
  data() {
    return {
      list: [],
      loading: false,
      detailVisible: false,
      detailLoading: false,
      detailList: [],
      currentRecord: null,
      approveRemark: '',
      queryParam: {
        returnNo: undefined,
        supplierName: undefined,
        returnStatus: undefined
      },
      pagination: {
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        showTotal: total => `共 ${total} 条`,
        pageSizeOptions: ['10', '20', '30', '50', '100']
      },
      columns: [
        {
          title: '退货单号',
          dataIndex: 'returnNo',
          width: 180,
          align: 'center'
        },
        {
          title: '供应商',
          dataIndex: 'supplierName',
          width: 200,
          align: 'center'
        },
        {
          title: '退货数量',
          dataIndex: 'totalQuantity',
          width: 100,
          align: 'center'
        },
        {
          title: '退货金额',
          dataIndex: 'totalAmount',
          width: 120,
          scopedSlots: { customRender: 'totalAmount' },
          align: 'center'
        },
        {
          title: '退货原因',
          dataIndex: 'returnReason',
          width: 100,
          scopedSlots: { customRender: 'returnReason' },
          align: 'center'
        },
        {
          title: '状态',
          dataIndex: 'returnStatus',
          width: 100,
          scopedSlots: { customRender: 'returnStatus' },
          align: 'center'
        },
        {
          title: '申请时间',
          dataIndex: 'applyTime',
          width: 180,
          scopedSlots: { customRender: 'applyTime' },
          align: 'center'
        },
        {
          title: '操作',
          dataIndex: 'operation',
          width: 280,
          scopedSlots: { customRender: 'operation' },
          align: 'center',
          fixed: 'right'
        }
      ],
      detailColumns: [
        {
          title: '商品信息',
          dataIndex: 'goodsName',
          key: 'goodsName',
          width: 300,
          ellipsis: true,
          scopedSlots: { customRender: 'goodsName' },
          align: 'center'
        },
        {
          title: '退货数量',
          dataIndex: 'returnCount',
          key: 'returnCount',
          width: 100,
          scopedSlots: { customRender: 'returnCount' },
          align: 'center'
        },
        {
          title: '单价(元)',
          dataIndex: 'unitPrice',
          key: 'unitPrice',
          width: 120,
          scopedSlots: { customRender: 'unitPrice' },
          align: 'center'
        },
        {
          title: '退货金额(元)',
          dataIndex: 'amount',
          key: 'amount',
          width: 130,
          scopedSlots: { customRender: 'amount' },
          align: 'center'
        },
        {
          title: '退货原因明细',
          dataIndex: 'returnReasonDetail',
          key: 'returnReasonDetail',
          width: 200,
          ellipsis: true,
          align: 'center'
        }
      ]
    }
  },
  created() {
    this.getList()
  },
  methods: {
    moment,
    parseTime(time) {
      return time ? moment(time).format('YYYY-MM-DD HH:mm:ss') : '-'
    },
    getList() {
      this.loading = true
      const params = {
        ...this.queryParam,
        current: this.pagination.current,
        size: this.pagination.pageSize
      }
      getReturnOrderList(params).then(response => {
        if (response.data) {
          this.list = response.data.records || []
          this.pagination.total = response.data.total || 0
        } else if (Array.isArray(response)) {
          this.list = response
        } else {
          this.list = []
          this.pagination.total = 0
        }
        this.loading = false
      }).catch(() => {
        this.loading = false
      })
    },
    handleQuery() {
      this.pagination.current = 1
      this.getList()
    },
    resetQuery() {
      this.queryParam = {
        returnNo: undefined,
        supplierName: undefined,
        returnStatus: undefined
      }
      this.pagination.current = 1
      this.getList()
    },
    handleTableChange(pagination) {
      this.pagination = pagination
      this.getList()
    },
    handleAdd() {
      this.$refs.returnForm.handleAdd()
    },
    handleDetail(record) {
      this.currentRecord = record
      this.detailVisible = true
      this.detailLoading = true
      getReturnItemList({
        returnOrderId: record.id
      }).then(response => {
        if (response.data) {
          this.detailList = Array.isArray(response.data) ? response.data : []
        } else if (Array.isArray(response)) {
          this.detailList = response
        } else {
          this.detailList = []
        }
        this.detailLoading = false
      }).catch(() => {
        this.detailLoading = false
      })
    },
    handleAudit(record) {
      this.$confirm({
        title: '确认审核通过',
        content: h => (
          <div>
            <p>确定要审核通过退货单【{record.returnNo}】吗?</p>
            <a-input
              v-model={this.approveRemark}
              placeholder="请输入审核备注（选填）"
              style={{ marginTop: '10px' }}
              maxlength="200"
              allowClear
            />
          </div>
        ),
        onOk: () => {
          auditReturnOrder({ returnOrderId: record.id, approveRemark: this.approveRemark }).then(() => {
            this.$message.success('审核通过成功')
            this.approveRemark = ''
            this.getList()
          }).catch(error => {
            this.$message.error('审核失败：' + (error.message || '未知错误'))
          })
        },
        onCancel: () => {
          this.approveRemark = ''
        }
      })
    },
    handleReject(record) {
      this.$confirm({
        title: '确认审核拒绝',
        content: h => (
          <div>
            <p>确定要拒绝退货单【{record.returnNo}】吗?</p>
            <a-input
              v-model={this.approveRemark}
              placeholder="请输入拒绝原因（选填）"
              style={{ marginTop: '10px' }}
              maxlength="200"
              allowClear
            />
          </div>
        ),
        onOk: () => {
          rejectReturnOrder({ returnOrderId: record.id, approveRemark: this.approveRemark }).then(() => {
            this.$message.success('已拒绝该退货申请')
            this.approveRemark = ''
            this.getList()
          }).catch(error => {
            this.$message.error('操作失败：' + (error.message || '未知错误'))
          })
        },
        onCancel: () => {
          this.approveRemark = ''
        }
      })
    },
    getStatusText(status) {
      const statusMap = {
        0: '待审核',
        1: '审核通过',
        2: '审核不通过',
        3: '已取消',
        4: '已退货入库'
      }
      return statusMap[status] || '未知'
    },
    getStatusColor(status) {
      const colorMap = {
        0: 'orange',
        1: 'green',
        2: 'red',
        3: 'gray',
        4: 'blue'
      }
      return colorMap[status] || 'default'
    },
    getReasonText(reason) {
      const reasonMap = {
        1: '质量问题',
        2: '错发漏发',
        3: '滞销退货',
        4: '其他'
      }
      return reasonMap[reason] || '-'
    }
  }
}
</script>
